import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useTheme } from 'styled-components'

import { Text, ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import PortalsInUseList from '@/configuration/components/PortalsInUseList/PortalsInUseList'
import SettingsSection from '@/configuration/components/SettingsSection/SettingsSection'

import { sectionTypes } from '../constants'
import useSettingsType from '../hooks/useSettingsType/useSettingsType'
import BrandAndImages from './BrandAndImages/BrandAndImages'
import BrandingPreview from './BrandingPreview/BrandingPreview'
import BrandingTabs from './BrandingTabs/BrandingTabs'
import ButtonsAndLinks from './ButtonsAndLinks/ButtonsAndLinks'
import ButtonsPreview from './ButtonsAndLinks/ButtonsPreview/ButtonsPreview'
import { buttonSubTabs, subTabToColorGroup } from './ButtonsAndLinks/constants'
import useBrandingTabs from './hooks/useBrandingTabs/useBrandingTabs'
import usePreviewMessaging from './hooks/usePreviewMessaging/usePreviewMessaging'
import usePreviewSection from './hooks/usePreviewSection/usePreviewSection'
import useThemeConfiguration from './hooks/useThemeConfiguration/useThemeConfiguration'
import { selectorSubTabs, subTabToColorGroup as selectorSubTabToColorGroup } from './Selectors/constants'
import Selectors from './Selectors/Selectors'
import SelectorsPreview from './Selectors/SelectorsPreview/SelectorsPreview'
import ThemeConfigurationSkeleton from './ThemeConfigurationSkeleton/ThemeConfigurationSkeleton'
import { brandingTabs } from './constants'
import { getErrorTabsFromResponse } from './helpers'
import * as Styles from './styles'

const ThemeConfiguration = ({ isUnifiedDashboard }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { info, updateContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)

  const {
    settingsType,
    setSettingsType,
    hasChanges: settingsTypeHasChanges,
    revertSettingsType,
    onSaveSettingsType,
    loadingStoreSettingsType,
  } = useSettingsType({
    returnPortalId: info?.selectedReturnPortal?.id,
    section: sectionTypes.theme,
    isUnifiedDashboard,
    setToastInfo,
  })

  const { activeTab, handleChangeTab, tabErrors, setTabError, subTabErrors, setSubTabError } = useBrandingTabs()

  const handleSaveError = error => {
    const { tabErrors, subTabErrors } = getErrorTabsFromResponse(error)
    Object.entries(tabErrors).forEach(([tab, hasError]) => setTabError(tab, hasError))
    Object.entries(subTabErrors).forEach(([subTab, hasError]) => setSubTabError(subTab, hasError))
  }

  const {
    handleOnSave,
    applyingTheme,
    gettingShopInfo,
    merchantTheme,
    loadingTheme,
    onCancelChanges,
    shouldShowHasChanges,
    colors,
    changeColor,
    images,
    deleteImage,
    changeImage,
    changeLink,
    isDisabledByGlobalSettings,
    isThemeValid,
    hasPermission,
    hasChanges,
    trackingPreviewUrl,
  } = useThemeConfiguration({
    merchantId: info?.id,
    storeId: info?.selectedStore?.id,
    returnPortalId: info?.selectedReturnPortal?.id,
    onSaveSettingsType,
    settingsTypeHasChanges,
    isUnifiedDashboard,
    setToastInfo,
    updateContext,
    t,
    settingsType,
    revertSettingsType,
    onSaveError: handleSaveError,
  })

  const [activeSubTab, setActiveSubTab] = useState(buttonSubTabs.primaryButtons)
  const [activeSelectorSubTab, setActiveSelectorSubTab] = useState(selectorSubTabs.methodRequest)

  const handleSubTabChange = tab => {
    setActiveSubTab(tab)
  }

  const handleSelectorSubTabChange = tab => {
    setActiveSelectorSubTab(tab)
  }

  const { activePreviewSection, handlePreviewSection, previewUrl } = usePreviewSection({
    trackingPreviewUrl,
    returnsPortalUrl: info?.selectedStore?.returns_portal_url,
  })

  const { iframeRef } = usePreviewMessaging({
    colors,
    images,
    previewUrl,
    activePreviewSection,
  })

  const handleChangeSettingsType = type => {
    setSettingsType(type)
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case brandingTabs.brandAndImages:
        return (
          <BrandAndImages
            changeImage={changeImage}
            deleteImage={deleteImage}
            images={images}
            changeLink={changeLink}
            disabled={isDisabledByGlobalSettings}
            onPreviewSection={handlePreviewSection}
            activePreviewSection={activePreviewSection}
            trackingPreviewUrl={trackingPreviewUrl}
            brandColors={colors.brandColors}
            changeColor={changeColor}
          />
        )
      case brandingTabs.buttonsAndLinks:
        return (
          <ButtonsAndLinks
            colors={{
              buttonColors: colors.buttonColors,
              buttonSimpleColors: colors.buttonSimpleColors,
              linkColors: colors.linkColors,
            }}
            changeColor={changeColor}
            disabled={isDisabledByGlobalSettings}
            activeSubTab={activeSubTab}
            onSubTabChange={handleSubTabChange}
            subTabErrors={subTabErrors}
          />
        )
      case brandingTabs.selectors:
        return (
          <Selectors
            colors={{
              splitButtonColors: colors.splitButtonColors,
              variantButtonColors: colors.variantButtonColors,
            }}
            changeColor={changeColor}
            disabled={isDisabledByGlobalSettings}
            activeSubTab={activeSelectorSubTab}
            onSubTabChange={handleSelectorSubTabChange}
            subTabErrors={subTabErrors}
          />
        )
      default:
        return null
    }
  }

  if (loadingTheme || loadingStoreSettingsType) {
    return <ThemeConfigurationSkeleton />
  }

  return (
    <Styles.Container>
      {(hasChanges || settingsTypeHasChanges) && (
        <UnsavedChangesAlert hasPermission={hasPermission(userPermissions.editBranding)} />
      )}
      {isUnifiedDashboard && (
        <Styles.PortalsInUseContainer>
          <PortalsInUseList portals={info.returnPortals} portalsInUse={merchantTheme?.enabled_portals} />
        </Styles.PortalsInUseContainer>
      )}
      {!isUnifiedDashboard && info.allows_unified_dashboard && (
        <SettingsSection type={settingsType} onChange={handleChangeSettingsType} />
      )}
      <Text type="bodyMRegular" color={theme.colors.text.default}>
        {t('Personaliza el producto para adaptarlo a la imagen de marca de tu tienda')}:
      </Text>
      <BrandingTabs activeTab={activeTab} onChangeTab={handleChangeTab} tabErrors={tabErrors} />
      <Styles.ContentLayout>
        <Styles.LeftColumn>{renderTabContent()}</Styles.LeftColumn>
        <Styles.RightColumn>
          {activeTab === brandingTabs.buttonsAndLinks && (
            <ButtonsPreview colors={colors[subTabToColorGroup[activeSubTab]]} activeSubTab={activeSubTab} />
          )}
          {activeTab === brandingTabs.selectors && (
            <SelectorsPreview
              colors={colors[selectorSubTabToColorGroup[activeSelectorSubTab]]}
              activeSubTab={activeSelectorSubTab}
            />
          )}
          {activeTab === brandingTabs.brandAndImages && (
            <BrandingPreview iframeRef={iframeRef} previewUrl={previewUrl} />
          )}
        </Styles.RightColumn>
      </Styles.ContentLayout>
      <SaveChangesButtons
        hasChanges={shouldShowHasChanges}
        onSave={handleOnSave}
        onRevertChanges={onCancelChanges}
        loading={applyingTheme || gettingShopInfo}
        disabled={!isThemeValid || applyingTheme || gettingShopInfo}
      />
    </Styles.Container>
  )
}

export default ThemeConfiguration
